import React, { useEffect, useState } from "react";
import {
  Box,
  Image,
  Text,
  Flex,
  Spinner,
  Icon,
  Link,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import { FaInstagram } from "react-icons/fa";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";

import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/pagination";

const InstagramFeed = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const bg = useColorModeValue("white", "gray.800");

  // determine slidesPerView at each breakpoint
  const slidesPerView = useBreakpointValue({ base: 2, md: 3, lg: 4 }) || 2;
  // center only when there are fewer items than slots
  const shouldCenter = media.length > 0 && media.length < slidesPerView;

  useEffect(() => {
    const fetchInstagramFeed = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch('/instagram/feed');
        const result = await response.json();
        
        if (result.success) {
          setMedia(result.data);
        } else {
          setError(result.message || 'Failed to fetch Instagram feed');
          console.error('Instagram feed error:', result.message);
        }
      } catch (err) {
        setError('Failed to connect to Instagram feed');
        console.error('Instagram fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramFeed();
  }, []);

  if (loading) {
    return (
      <Flex
        w="100%"
        h="300px" // match carousel height (≈260px + padding)
        justifyContent="center"
        alignItems="center"
      >
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box mt={4} mb={2} textAlign="center" px={{ base: 4, md: 8 }}>
        <Link
          href="https://www.instagram.com/nomad360.io/"
          isExternal
          _hover={{ textDecoration: "underline" }}
        >
          <Text
            fontSize="xl"
            mb={4}
            fontWeight="bold"
            display="inline-flex"
            alignItems="center"
            gap={2}
          >
            <Icon as={FaInstagram} /> INSTAGRAM
          </Text>
        </Link>
        <Text color="gray.500" fontSize="sm">
          {error}
        </Text>
      </Box>
    );
  }

  return (
    <Box mt={4} mb={2} textAlign="center" px={{ base: 4, md: 8 }}>
      <Link
        href="https://www.instagram.com/nomad360.io/"
        isExternal
        _hover={{ textDecoration: "underline" }}
      >
        <Text
          fontSize="xl"
          mb={4}
          fontWeight="bold"
          display="inline-flex"
          alignItems="center"
          gap={2}
        >
          <Icon as={FaInstagram} /> INSTAGRAM
        </Text>
      </Link>

      <Swiper
        modules={[Autoplay, Pagination]}
        autoplay={{ delay: 2500, disableOnInteraction: false }}
        loop={media.length >= slidesPerView} // only loop when enough
        slidesPerView={slidesPerView} // default for all breakpoints
        centeredSlides={shouldCenter} // center when few
        centeredSlidesBounds
        breakpoints={{
          0: { slidesPerView: 2, spaceBetween: 12 },
          640: { slidesPerView: 3, spaceBetween: 16 },
          1024: { slidesPerView: 4, spaceBetween: 20 },
        }}
        pagination={{ clickable: true }}
        style={{ paddingBottom: "2.5rem" }}
      >
        {media.map((item) => (
          <SwiperSlide key={item.id}>
            <Box
              as="a"
              href={item.permalink}
              target="_blank"
              rel="noopener noreferrer"
              borderRadius="md"
              boxShadow="md"
              overflow="hidden"
              bg={bg}
              _hover={{ transform: "scale(1.05)" }}
              transition="transform 0.2s ease"
            >
              <Image
                src={item.media_url}
                alt={item.caption || "Instagram image"}
                w="100%"
                h="260px"
                objectFit="cover"
              />
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default InstagramFeed;
